import {
  useState,
  useEffect,
  useContext,
  createContext,
  ReactNode,
  useMemo,
} from 'react';
import ConvertSDK, { type ContextInterface } from '@convertcom/js-sdk';

import { useUserContext } from './UserContext';

import { rg4js } from '../helpers/raygun';
import { PAGE } from '../helpers/pages';

type UseConvertContextParameters = { page: PAGE };

type ConvertProviderParameters = { children: ReactNode };

const ConvertContext = createContext<{
  sdkContext: ContextInterface | null | undefined;
} | null>(null);

const convertSDK = new ConvertSDK({
  sdkKey: '10042404/100416932',
  environment: 'production',
});

/**
 * This function allows the Convert SDK context to be accessed anywhere within the application
 * in order to run A/B experiments that require code changes.
 *
 * This may include displaying different components and executing different functions
 * depending on variations within the experiment.
 */
const ConvertProvider = (params: ConvertProviderParameters) => {
  const { children } = params;

  const [sdkContext, setSdkContext] = useState<
    ContextInterface | null | undefined
  >();
  const {
    consentOptions: { allowConvert },
    user,
  } = useUserContext();

  useEffect(() => {
    const initSdk = async (userId: string) => {
      try {
        await convertSDK.onReady();
        const context = convertSDK.createContext(userId);
        setSdkContext(context);
      } catch (error) {
        setSdkContext(null);
        rg4js('send', {
          error: new Error('Error loading Convert SDK'),
          tags: [PAGE.APP],
          ...(error.message
            ? {
                customData: {
                  message: error.message ?? '',
                },
              }
            : {}),
        });
      }
    };
    if (allowConvert && user?.id) {
      initSdk(user.id.toString());
    } else {
      setSdkContext(null);
    }
  }, [allowConvert, user?.id]);

  const contextValue = useMemo(
    () => ({
      sdkContext,
    }),
    [sdkContext],
  );

  return (
    <ConvertContext.Provider value={contextValue}>
      {children}
    </ConvertContext.Provider>
  );
};

const useConvertContext = (params: UseConvertContextParameters) => {
  const { page } = params;
  const context = useContext(ConvertContext);
  if (!context) {
    const error = new Error(
      'useConvertContext must be used within an instance of ConvertProvider',
    );
    rg4js('send', {
      error,
      tags: [page],
    });
    throw error;
  }
  return context;
};

export { ConvertProvider, useConvertContext, ConvertContext };
